import type {
    ImageSearchErrorCode,
    SubmitImageSearchResult,
} from '@/redux/modules/search/imageSearch/imageSearch.types';

import { AsyncThunkConfigWithRejectValue, createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { getClientIpAddress } from '@/redux/modules/browser';
import { getDeployment } from '@/redux/modules/config';
import { getUserExcludedFacets } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { fetchImageSearchResults } from '@/redux/modules/search/imageSearch/imageSearch.api';
import { trackSearchByImageResultsLoadedAnalytics } from '@/utils/amplitude/analytics/searchByImage/trackSearchByImageResultsLoaded.analytics';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';
import { resizeImageToBase64 } from '@/utils/image/resizeImageToBase64';

const IMAGE_SEARCH_COUNT = 48;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

/**
 * Resizes the selected image, base64-encodes it, and runs one image search through search-party.
 * On success returns the hydrated cards plus an object URL for the resized image (the results-page
 * thumbnail). Errors collapse to a stable code the UI maps to localized copy.
 */
export const submitImageSearch = createAsyncThunk<
    SubmitImageSearchResult,
    File,
    AsyncThunkConfigWithRejectValue<ImageSearchErrorCode>
>('la/ui/imageSearch/submitImageSearch', async (file, { getState, rejectWithValue }) => {
    // Empty type means the browser couldn't classify it (e.g. some HEIC) — let the resize/BE decide.
    if (file.type && !ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        return rejectWithValue('invalid-type');
    }

    const state = getState();
    const deployment = getDeployment(state);
    let thumbnailUrl: string | undefined;
    try {
        const { base64, blob } = await resizeImageToBase64(file);
        thumbnailUrl = URL.createObjectURL(blob);

        const { payload } = await fetchImageSearchResults({
            authToken: getAuthToken(state),
            count: IMAGE_SEARCH_COUNT,
            deployment,
            excludedAuctioneers: getUserExcludedFacets(state).auctionHouse,
            imageData: base64,
            ipAddress: getClientIpAddress(state),
        });

        const results = payload.items ?? [];

        // Fire once per completed search on the success path (covers the zero-result case);
        // the pathname reflects the page the search was submitted from, like Search Completed.
        const { pathname } = window.location;
        trackSearchByImageResultsLoadedAnalytics({
            numberOfSearchResults: results.length,
            pageName: getAmplitudePageNameFromPath(pathname),
            url: getFullUrl(deployment, pathname),
        });

        return { results, thumbnailUrl };
    } catch {
        if (thumbnailUrl) {
            URL.revokeObjectURL(thumbnailUrl);
        }
        return rejectWithValue('request-failed');
    }
});
